import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import cn from "classnames";
import { useTranslation } from 'react-i18next';
import Skeleton from "@components/new/Skeleton";
import ProgressBar from '@components/ProgressBar';
import { P1, Title3 } from "@components/new/Typo";
import { HoverCard } from '@components/new/HoverCard';
import { getUniClubLevels } from '@api/apiClient/club';
import { useProfile } from '@contexts/ProfileContext';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import Icon from "@components/new/Icon";
import ChevronRightIcon from '@assets/icons/outline/chevron-right.svg?react';
import StarIcon from "@assets/icons/outline/star.svg?react";
import { createSumFromObject } from '@utils/formatValues';
import styles from './ClubWidget.module.scss';

const ClubWidget = () => {
  const { sellersNotFound } = useSellersCounter();
  const { account } = useProfile();
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["getUniClubLevels"],
    queryFn: getUniClubLevels,
    retry: 1,
    select: (data) => ({
      ...data,
      level: data.levels.map((item, idx) => ({
        ...item,
        name: item.name.toLowerCase(),
        nextLevelFrom: data.levels[idx + 1]?.spentFrom,
        cashbackPercent: item.cashbackPercent * 100,
      })).find((item) => (
        (item.spentFrom?.amount <= data?.currentSpend?.amount)
        && (!item.nextLevelFrom || item.nextLevelFrom?.amount > data?.currentSpend?.amount)
      )),
    }),
    enabled: !sellersNotFound && (account?.region === "TR"),
  });

  if (sellersNotFound || account?.region !== "TR") return null;
  if (isLoading) {
    return (
      <Skeleton width="110px" height="18px" />
    )
  }

  const currentSpendAmount = data?.currentSpend?.amount ?? 0;
  const spentFromAmount = data?.level?.spentFrom?.amount ?? 0;
  const nextLevelFromAmount = data?.level?.nextLevelFrom?.amount ?? 0;

  const levelName = data?.level?.name ?? "base";

  return (
    <HoverCard
      Action={() => (
        <Link
          className={styles.clubWidget}
          to="/uni-club"
        >
          <div className={cn(styles.iconContainer, styles[`level-${levelName}`])}>
            <StarIcon />
          </div>
          <div className={styles.bar}>
            <ProgressBar
              colors={[
                {
                  value: 0,
                  main: styles[levelName],
                  bg: styles.bg,
                }
              ]}
              progress={
                nextLevelFromAmount
                  ? ((currentSpendAmount - spentFromAmount) * 100 / (nextLevelFromAmount - spentFromAmount))
                  : 100
              }
              rounded
            />
          </div>
        </Link>
      )}
      Content={() => (
        <div className={styles.hoverCard}>
          <div className={styles.main}>
            <P1 color="error-sade">
              {t(
                'clubWidget.title',
                {
                  value: `${data?.level?.cashbackPercent ?? 0} %`,
                  context: levelName,
                }
              )}
            </P1>
            <Title3
              color="grey-800"
              className={cn(styles.description, styles[`description-${levelName}`])}
              dangerouslySetInnerHTML={{
                __html: t('clubWidget.description', {
                  earned: data?.currentCashback ? createSumFromObject(data?.currentCashback) : "-",
                  parcelsSent: "-",
                  parcelsTotal: "-",
                  daysLeft: "-",
                  daysTotal: "30",
                }),
              }}
            />
          </div>
          <div className={styles.divider} />
          <div className={styles.footer}>
            <LinkButton
              to={'/uni-club'}
              title={
                <>
                  {t("clubWidget.detailsLink")}
                  <Icon icon={ChevronRightIcon} />
                </>
              }
              size={LinkButtonSize.M}
            />
          </div>
        </div>
      )}
    />
  );
};

export default ClubWidget;
